import { useState, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsis,
  faPenToSquare,
  faCircleMinus,
  faPlus,
  faArrowDownShortWide,
  faArrowDownWideShort,
  faSearch,
  faCircleCheck,
  faFlag,
  faClock,
  faSquareParking,
  faLocationDot,
} from "@fortawesome/pro-regular-svg-icons";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import CustomDropdown from "../../components/CustomDropdown";
import InputField from "../../components/InputField";
import Tooltip from "../../components/Tooltip";
import TariffPriceTooltip, { clearTariffPriceCache } from "../../components/tariff/TariffPriceTooltip";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import { getTariffList, deleteTariff } from "../../api/Tariffs";
import { getStatusChipStyleVars } from "../../theme/gitoColors";
import { formatSwedishDate, getUiDateLocale } from "../../helpers/DateTime";
import { useTranslation } from "react-i18next";

const MODEL_LABEL_KEYS = {
  FIXED: "tariff.modeFixedTitle",
  TIME: "tariff.modeTimeTitle",
  POWER: "tariff.modePowerTitle",
  SPOT: "tariff.modeSpotTitle",
};

const STATUS_STYLE_KEYS = {
  ACTIVE: "ACTIVE",
  UPCOMING: "PENDING",
  EXPIRED: "EXPIRED",
};

const FILTERS = [
  { key: "all", labelKey: "common.all", styleKey: "DEFAULT", match: () => true },
  { key: "public", labelKey: "tariff.list.filterPublic", styleKey: "INFO", match: (tariff) => tariff.isPublic },
  { key: "customer", labelKey: "tariff.list.filterCustomer", styleKey: "RESERVED", match: (tariff) => !tariff.isPublic },
  { key: "spot", labelKey: "tariff.list.filterSpot", styleKey: "OTHER", match: (tariff) => tariff.pricingModel === "SPOT" },
  { key: "notAssigned", labelKey: "tariff.list.filterNotAssigned", styleKey: "WARNING", match: (tariff) => tariff.sites.length === 0 },
  { key: "expired", labelKey: "tariff.list.filterExpired", styleKey: "EXPIRED", match: (tariff) => tariff.status === "EXPIRED" },
];

// Values used when sorting each column.
const SORT_VALUES = {
  tariffName: (tariff) => tariff.tariffName?.toLowerCase() ?? "",
  type: (tariff) => (tariff.isPublic ? -1 : tariff.customers.length),
  pricingModel: (tariff) => tariff.pricingModel ?? "",
  energyPrice: (tariff) => tariff.energyPriceMinInclVat ?? tariff.spotMarkupInclVat ?? Number.MAX_VALUE,
  preauthorizeAmount: (tariff) => tariff.preauthorizeAmount ?? -1,
  sites: (tariff) => tariff.sites.length,
  status: (tariff) => tariff.status ?? "",
};

const TariffsPage = () => {
  const { t, i18n } = useTranslation();
  const { getAccessToken } = useAuth();
  const [tariffs, setTariffs] = useState([]);
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    confirm: () => {},
  });
  const navigate = useNavigate();
  const [tariffsLoaded, setTariffsLoaded] = useState(false);
  const numberLocale = getUiDateLocale(i18n.language);

  useEffect(() => {
    handleGetTariffs();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, []);

  const filteredTariffs = useMemo(() => {
    const filter = FILTERS.find((f) => f.key === activeFilter) ?? FILTERS[0];
    const searchLower = search.toLowerCase();

    const filtered = tariffs.filter((tariff) => {
      if (!filter.match(tariff)) {
        return false;
      }
      if (!searchLower) {
        return true;
      }
      return (
        tariff.tariffPublicId.toLowerCase().includes(searchLower) ||
        tariff.tariffName?.toLowerCase().includes(searchLower) ||
        tariff.customers.some((customer) => customer.name?.toLowerCase().includes(searchLower)) ||
        tariff.sites.some((site) => site.name?.toLowerCase().includes(searchLower))
      );
    });

    const getValue = SORT_VALUES[sortColumn];
    if (!getValue) {
      return filtered;
    }

    return [...filtered].sort((a, b) => {
      const aValue = getValue(a);
      const bValue = getValue(b);
      if (aValue === bValue) {
        return 0;
      }
      const result = aValue > bValue ? 1 : -1;
      return sortOrder === "asc" ? result : -result;
    });
  }, [search, tariffs, sortColumn, sortOrder, activeFilter]);

  const filterCounts = useMemo(
    () => Object.fromEntries(FILTERS.map((f) => [f.key, tariffs.filter(f.match).length])),
    [tariffs]
  );

  const handleGetTariffs = async () => {
    try {
      const accessToken = await getAccessToken();
      clearTariffPriceCache();
      setTariffs(await getTariffList(accessToken));
      setTariffsLoaded(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteTariff = async (tariffPublicId) => {
    const toastId = toast.loading(t("toast.deleting", { entity: t("entity.tariff") }));
    try {
      const accessToken = await getAccessToken();
      await deleteTariff(tariffPublicId, accessToken);
      clearTariffPriceCache();
      setTariffs(await getTariffList(accessToken));
      toast.success(t("toast.deleted", { entity: t("entity.tariff") }), { id: toastId });
    } catch (error) {
      toast.error(error?.message ?? t("toast.failedDelete", { entity: t("entity.tariff") }), { id: toastId });
      console.error(error);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const formatAmount = (value, currency, unit = "") => {
    if (value == null) {
      return "";
    }
    const amount = Number(value).toLocaleString(numberLocale, { maximumFractionDigits: 2 });
    return `${amount} ${currency ?? ""}${unit}`.trim();
  };

  const renderSortableHeader = (column, label, className = "") => (
    <th className={`text-left cursor-pointer whitespace-nowrap ${className}`} onClick={() => handleSort(column)}>
      {label}
      {sortColumn === column && (
        <FontAwesomeIcon
          icon={sortOrder === "asc" ? faArrowDownShortWide : faArrowDownWideShort}
          className="w-4 pl-2"
        />
      )}
    </th>
  );

  const renderListTooltip = (title, rows) => (
    <div className="min-w-48">
      <div className="font-semibold mb-1">{title}</div>
      {rows.map((row) => (
        <div key={row.key} className="flex justify-between gap-4 border-t border-mute-200 py-1">
          <span>{row.label}</span>
          {row.value ? <span className="text-mute-600 whitespace-nowrap">{row.value}</span> : null}
        </div>
      ))}
    </div>
  );

  const renderType = (tariff) => {
    if (tariff.isPublic) {
      return (
        <span className="badge" style={getStatusChipStyleVars("INFO")}>
          {t("tariff.list.typePublic")}
        </span>
      );
    }
    const badge = (
      <span className="badge cursor-help" style={getStatusChipStyleVars("RESERVED")}>
        {t("tariff.list.typeCustomer", { count: tariff.customers.length })}
      </span>
    );
    if (tariff.customers.length === 0) {
      return badge;
    }
    return (
      <Tooltip
        content={renderListTooltip(
          `${t("entity.customers")} (${tariff.customers.length})`,
          tariff.customers.map((customer) => ({ key: customer.customerPublicId, label: customer.name }))
        )}
      >
        {badge}
      </Tooltip>
    );
  };

  const renderEnergyPrice = (tariff) => {
    let main = "";
    let sub = "";
    if (tariff.pricingModel === "SPOT") {
      main = t("tariff.list.spotMarkup", {
        price: formatAmount(tariff.spotMarkupInclVat ?? 0, tariff.currency, "/kWh"),
      });
      if (tariff.spotMinInclVat != null || tariff.spotMaxInclVat != null) {
        sub = t("tariff.list.spotBounds", {
          min: tariff.spotMinInclVat != null ? formatAmount(tariff.spotMinInclVat, "") : "–",
          max: tariff.spotMaxInclVat != null ? formatAmount(tariff.spotMaxInclVat, "") : "–",
          unit: `${tariff.currency ?? ""}/kWh`,
        });
      }
    } else if (tariff.energyPriceMinInclVat != null) {
      main =
        tariff.energyPriceMinInclVat === tariff.energyPriceMaxInclVat
          ? formatAmount(tariff.energyPriceMinInclVat, tariff.currency, "/kWh")
          : `${formatAmount(tariff.energyPriceMinInclVat, "")}–${formatAmount(tariff.energyPriceMaxInclVat, tariff.currency, "/kWh")}`;
    }

    if (!main) {
      return <span className="text-mute-500">–</span>;
    }

    return (
      <Tooltip
        content={
          <TariffPriceTooltip
            tariffPublicId={tariff.tariffPublicId}
            title={t(MODEL_LABEL_KEYS[tariff.pricingModel] ?? "tariff.list.energyPriceInclVat")}
          />
        }
      >
        <span className="cursor-help border-b border-dotted border-mute-400">
          {main}
          {sub && <span className="block text-xs text-mute-500">{sub}</span>}
        </span>
      </Tooltip>
    );
  };

  const renderExtras = (tariff) => {
    const extras = [
      tariff.hasFlat && { key: "flat", icon: faFlag, label: t("tariff.startFee") },
      tariff.hasTime && { key: "time", icon: faClock, label: t("tariff.chargingTime") },
      tariff.hasParking && { key: "parking", icon: faSquareParking, label: t("tariff.parkingTime") },
    ].filter(Boolean);
    if (extras.length === 0) {
      return <span className="text-mute-500">–</span>;
    }
    return (
      <span className="flex gap-2">
        {extras.map((extra) => (
          <Tooltip key={extra.key} content={extra.label}>
            <FontAwesomeIcon icon={extra.icon} className="text-mute-600" aria-label={extra.label} />
          </Tooltip>
        ))}
      </span>
    );
  };

  const renderSites = (tariff) => {
    if (tariff.sites.length === 0) {
      return (
        <span className="badge" style={getStatusChipStyleVars("WARNING")}>
          {t("tariff.list.notAssigned")}
        </span>
      );
    }
    return (
      <Tooltip
        content={renderListTooltip(
          `${t("entity.sites")} (${tariff.sites.length})`,
          tariff.sites.map((site) => ({
            key: site.chargingSitePublicId,
            label: site.name,
            value: t("tariff.list.connectorCount", { count: site.connectorCount }),
          }))
        )}
      >
        <span className="badge cursor-help gap-1" style={getStatusChipStyleVars("DEFAULT")}>
          <FontAwesomeIcon icon={faLocationDot} />
          {tariff.sites.length}
        </span>
      </Tooltip>
    );
  };

  const renderStatus = (tariff) => {
    const labelKey = {
      ACTIVE: "common.active",
      UPCOMING: "tariff.list.statusUpcoming",
      EXPIRED: "tariff.list.statusExpired",
    }[tariff.status];
    const from = tariff.startDateTimeUtc ? formatSwedishDate(tariff.startDateTimeUtc) : "";
    const to = tariff.endDateTimeUtc ? formatSwedishDate(tariff.endDateTimeUtc) : "";
    let validity = "";
    if (from && to) {
      validity = t("tariff.list.validRange", { from, to });
    } else if (from) {
      validity = t("tariff.list.startsAt", { date: from });
    } else if (to) {
      validity = t("tariff.list.endsAt", { date: to });
    }
    return (
      <span>
        <span className="badge" style={getStatusChipStyleVars(STATUS_STYLE_KEYS[tariff.status] ?? "DEFAULT")}>
          {labelKey ? t(labelKey) : tariff.status}
        </span>
        {validity && <span className="block text-xs text-mute-500">{validity}</span>}
      </span>
    );
  };

  const openTariff = (tariff) => navigate(`/tariffs/${tariff.tariffPublicId}`);

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            <div className="absolute inset-0 pointer-events-none">
              <LoadingTablePage title={t("entity.tariffs")} loading={!tariffsLoaded} />
            </div>

            <div className={`${!tariffsLoaded ? "loading" : ""} page-content`}>
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">{t("entity.tariffs")}</h2>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    navigate(`/tariffs/create`);
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} /> {t("pages.createTariff")}
                </button>
              </div>
              <div className="rounded-md shadow-md w-full p-4">
                <div className="mb-2 flex w-full flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                    {FILTERS.map((filter) => (
                      <button
                        key={filter.key}
                        type="button"
                        onClick={() => setActiveFilter(filter.key)}
                        className={`chip ${activeFilter === filter.key ? "chip--active" : ""} shadow-md`}
                        style={getStatusChipStyleVars(filter.styleKey)}
                      >
                        <FontAwesomeIcon icon={faCircleCheck} className="chip__check text-xs" />
                        <span>{t(filter.labelKey)}</span>
                        <span className="chip__count">{filterCounts[filter.key]}</span>
                      </button>
                    ))}
                  </div>
                  <div>
                    <InputField
                      icon={faSearch}
                      placeholder={t("search.tariffs")}
                      value={search}
                      onChange={(v) => {
                        setSearch(v.target.value);
                      }}
                    />
                  </div>
                </div>
                <div className="overflow-x-auto pb-[250px] mb-[-250px]">
                  <table className="table-default w-full">
                    <thead>
                      <tr>
                        {renderSortableHeader("tariffName", t("common.name"))}
                        {renderSortableHeader("pricingModel", t("tariff.list.pricingModel"))}
                        {renderSortableHeader("energyPrice", t("tariff.list.energyPriceInclVat"))}
                        <th className="text-left whitespace-nowrap hidden md:table-cell">{t("tariff.list.extras")}</th>
                        {renderSortableHeader("preauthorizeAmount", t("tariff.list.preauth"), "hidden md:table-cell")}
                        {renderSortableHeader("type", t("tariff.list.type"))}
                        {renderSortableHeader("sites", t("entity.sites"))}
                        {renderSortableHeader("status", t("common.status"))}
                        <th className="text-center"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {tariffsLoaded && filteredTariffs.length === 0 && (
                        <tr>
                          <td colSpan={9} className="text-center text-mute-500">
                            {t("common.noResults")}
                          </td>
                        </tr>
                      )}
                      {filteredTariffs.map((tariff) => (
                        <tr key={tariff.tariffPublicId} className="cursor-pointer hover:bg-mute-50" onClick={() => openTariff(tariff)}>
                          <td>
                            {tariff.tariffName}
                            <span className="block text-xs text-mute-500">{tariff.tariffPublicId}</span>
                          </td>
                          <td className="whitespace-nowrap">
                            {tariff.pricingModel ? t(MODEL_LABEL_KEYS[tariff.pricingModel]) : "–"}
                          </td>
                          <td className="whitespace-nowrap">{renderEnergyPrice(tariff)}</td>
                          <td className="hidden md:table-cell">{renderExtras(tariff)}</td>
                          <td className="whitespace-nowrap hidden md:table-cell">
                            {tariff.preauthorizeAmount != null
                              ? formatAmount(tariff.preauthorizeAmount, tariff.currency)
                              : <span className="text-mute-500">–</span>}
                          </td>
                          <td>{renderType(tariff)}</td>
                          <td>{renderSites(tariff)}</td>
                          <td className="whitespace-nowrap">{renderStatus(tariff)}</td>

                          <td className="text-center flex justify-end" onClick={(event) => event.stopPropagation()}>
                            <CustomDropdown
                              isRight={true}
                              ContentComponent={() => {
                                return (
                                  <div className="w-40 flex flex-col items-start bg-white p-2 rounded-lg">
                                    <button
                                      className="block px-4 py-2 text-sm text-black hover:bg-mute-200 w-full text-left rounded-lg"
                                      onClick={() => openTariff(tariff)}
                                    >
                                      <FontAwesomeIcon icon={faPenToSquare} />{" "}
                                      {t("common.edit")}
                                    </button>
                                    <button
                                      className="block px-4 py-2 text-sm text-red-500 hover:bg-mute-200 w-full text-left rounded-lg"
                                      onClick={() => { setConfirmState({ isOpen: true, title: t("confirm.deleteEntity", { entity: t("entity.tariff"), name: tariff.tariffName }), confirm: () => { handleDeleteTariff(tariff.tariffPublicId) } }) }}
                                    >
                                      <FontAwesomeIcon icon={faCircleMinus} /> {t("common.delete")}
                                    </button>
                                  </div>
                                );
                              }}
                            >
                              <div className="w-8 h-6 flex justify-end bg-red items-center cursor-pointer">
                                <FontAwesomeIcon icon={faEllipsis} />
                              </div>
                            </CustomDropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
        }}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default TariffsPage;
